import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { submitLead, submitProspectCRM, getAgents, API } from "@/lib/api";
import { calculateFullEstimate } from "@/lib/calculator/calculate";
import { ASSUMPTIONS, FINANCE_OPTION_KEYS } from "@/lib/calculator/assumptions";
import {
  ArrowRight, ArrowLeft, Users, Droplets, Flame, Zap as ZapIcon,
  Sparkles, BadgeCheck, Shield, Wrench, Bath, Calendar, Cpu,
  CalendarClock, Wifi, HousePlug, Leaf, Banknote, Building2, LogOut
} from "lucide-react";
import CalcStepCard from "@/components/calculator/CalcStepCard";
import CalcChoiceCard from "@/components/calculator/CalcChoiceCard";
import CalcResults from "@/components/calculator/CalcResults";
import AddressAutocomplete from "@/components/forms/AddressAutocomplete";

const PRODUCT_IMG = "/products/neopower_lifestyle.png";

// Step labels per mode (1-based index after pop'd from this array)
const PUBLIC_STEPS = [
  "Your Home & Hot Water Habits",
  "Your Current Hot Water System",
  "Your Energy Costs & Savings Potential",
  "Your Best-Fit NeoPower Solution",
  "Get Your Personalised Assessment",
];

const STAFF_STEPS = [
  "AeroMax Lead Capture",
  ...PUBLIC_STEPS,
];

const SYSTEM_OPTIONS = [
  { v: "continuous_gas", title: "Continuous gas", desc: "Wall-mounted gas heater that fires on demand.", icon: Flame },
  { v: "gas_storage", title: "Gas storage", desc: "Floor-standing gas cylinder keeping water hot 24/7.", icon: Flame },
  { v: "electric_storage", title: "Electric storage cylinder", desc: "Classic Kiwi resistance-element tank.", icon: ZapIcon },
  { v: "lpg", title: "LPG bottle", desc: "Bottled LPG-fed gas heater.", icon: Flame },
  { v: "not_sure", title: "Not sure", desc: "We'll use conservative NZ household assumptions.", icon: Sparkles },
];

// CRM enum maps — values not present here are omitted from the payload.
const CRM_LEAD_SOURCE = "WEBSITE-SAVINGS-CALCULATOR";

// Staff "Source" → CRM leadSource (used when submitting in staff mode).
const STAFF_SOURCE_TO_CRM = {
  "Home Show": "HOME-SHOW-SAVINGS-CALCULATOR",
  "Website": "WEBSITE-SAVINGS-CALCULATOR",
  "Referral": "REFERRAL-LEADS",
  "Digital": "DIGITAL-MARKETING-SAVINGS-CALCULATOR",
  "Other": "OTHER",
};

const CRM_SYSTEM_MAP = {
  electric_storage: "ELECTRIC-CYLINDER",
  gas_storage: "GAS-STORAGE",
  continuous_gas: "GAS-CONTINUOUS",
  lpg: "LPG",
  not_sure: "UNSURE",
};

const CRM_USAGE_MAP = {
  low: "LOW",
  medium: "MEDIUM",
  high: "HIGH",
};

const SYSTEM_AGE_OPTIONS = [
  { v: "under_5", title: "Under 5 years" },
  { v: "5_10", title: "5–10 years" },
  { v: "10_15", title: "10–15 years" },
  { v: "15_plus", title: "15+ years" },
  { v: "not_sure", title: "Not sure" },
];

const BILL_RANGE_OPTIONS = [
  { v: "100_200", title: "$100–$200" },
  { v: "200_300", title: "$200–$300" },
  { v: "300_400", title: "$300–$400" },
  { v: "400_plus", title: "$400+" },
];

const LOWER_BILL_OPTIONS = [
  { v: "yes", title: "Yes", desc: "Definitely interested" },
  { v: "maybe", title: "Maybe", desc: "Want to learn more" },
  { v: "no", title: "No", desc: "Not for me right now" },
];

const FINANCE_OPTIONS = FINANCE_OPTION_KEYS.map((k) => ({ v: k, title: ASSUMPTIONS.financeOptions[k].label }));

const STAFF_INSTALLERS = [
  { v: "none", label: "No installation partner selected" },
  { v: "tbc", label: "To be confirmed" },
  { v: "other", label: "Other / manually enter partner name" },
];
const STAFF_SOURCES = ["Home Show", "Website", "Referral", "Digital", "Other"];
const STAFF_HOME_SHOWS = ["The Star Home Show Christchurch", "Other"];
const STAFF_DIGITAL_SOURCES = ["Facebook", "Instagram", "Google", "TikTok", "Other"];

const STAFF_MODE_KEY = "aeromax_staff_mode";

export default function Calculator() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Staff mode resolution:
  //  • URL ?mode=staff → activate and persist to localStorage
  //  • Otherwise, fall back to localStorage flag set by an earlier visit
  const urlStaff = searchParams.get("mode") === "staff";
  const [isStaffMode, setIsStaffMode] = useState(() => {
    if (urlStaff) return true;
    if (typeof window !== "undefined") {
      return window.localStorage.getItem(STAFF_MODE_KEY) === "1";
    }
    return false;
  });

  // Persist staff mode whenever the URL activates it
  useEffect(() => {
    if (urlStaff) {
      window.localStorage.setItem(STAFF_MODE_KEY, "1");
      setIsStaffMode(true);
    }
  }, [urlStaff]);

  // Load CRM specialists (agents) once staff mode is active.
  useEffect(() => {
    if (!isStaffMode) return;
    let cancelled = false;
    getAgents().then((list) => { if (!cancelled) setAgents(list); });
    return () => { cancelled = true; };
  }, [isStaffMode]);

  const exitStaffMode = () => {
    window.localStorage.removeItem(STAFF_MODE_KEY);
    setIsStaffMode(false);
    // Strip ?mode=staff from the URL while staying on the page
    const params = new URLSearchParams(searchParams);
    params.delete("mode");
    setSearchParams(params, { replace: true });
    setStep(0);
    setResult(null);
  };

  const stepsList = isStaffMode ? STAFF_STEPS : PUBLIC_STEPS;
  const totalSteps = stepsList.length;

  // Calc inputs — preserved structure from the existing API.
  // Phase 2 fix: numeric inputs start as "" so the field renders blank with a
  // helpful placeholder instead of showing "0" the user has to delete first.
  const [inputs, setInputs] = useState({
    people: "", showers_per_day: "", shower_length_min: "", shower_flow_lpm: 9,
    other_usage: "medium", bathrooms: "", bath_use: "occasional",
    current_system: "electric_storage", system_age: "10_15",
    bill_range: "200_300", lower_bill_interest: "yes",
    electricity_c_per_kwh: "", gas_price: "", gas_unit: "c_per_mj",
    lpg_bottle_cost: "", daily_gas_charge_cents: "",
    remove_gas_connection: false,
    finance_option: "none",
    cop_scenario: "typical", // hidden — defaults to typical
  });

  // Customer details (Step 5/6)
  const [lead, setLead] = useState({
    name: "", email: "", phone: "", address: "",
    // Separate address parts (auto-filled from the autocomplete, still editable).
    // Sent to the CRM as discrete fields; the backend lead keeps the full address.
    suburb: "", city: "", postcode: "",
    address_details: null, // { formatted_address, street_address, sublocality, locality, region, postal_code, country, lat, lng } when chosen from autocomplete
    consent: false,
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [leadId, setLeadId] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null); // null | "sending" | "sent" | "failed"
  const [crmProspectId, setCrmProspectId] = useState(null); // AeroMax CRM prospect id
  const [agents, setAgents] = useState([]); // CRM specialists (staff mode)

  // Staff capture (Step 1 in staff mode)
  const [staffCapture, setStaffCapture] = useState({
    specialist: "",      // display name (or "Other")
    specialist_id: "",   // CRM agent id → assignAgentId
    specialist_other: "",
    installer: "none",
    installer_other: "",
    date_initiation: new Date().toISOString().slice(0, 10),
    source: "Home Show",
    home_show: "The Star Home Show Christchurch",
    home_show_other: "",
    referral_code: "",
    referral_name: "",
    digital_source: "Facebook",
    digital_source_other: "",
    source_other: "",
  });

  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const setStaff = (k, v) => setStaffCapture((s) => ({ ...s, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const currentLabel = stepsList[step];

  // Map the visible step number to internal step content key
  // Staff mode: step 0=staff-lead, 1=habits, 2=current-system, 3=energy, 4=best-fit, 5=assessment
  // Public mode: step 0=habits, 1=current-system, 2=energy, 3=best-fit, 4=assessment
  const stepKey = useMemo(() => {
    if (isStaffMode) {
      return ["staff-lead", "habits", "current-system", "energy", "best-fit", "assessment"][step];
    }
    return ["habits", "current-system", "energy", "best-fit", "assessment"][step];
  }, [step, isStaffMode]);

  const isLastStep = step === totalSteps - 1;

  const validateLead = () => {
    const errs = {};
    if (!lead.name.trim()) errs.name = "Please enter your full name.";
    if (!lead.email.trim()) errs.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim()))
      errs.email = "Please enter a valid email address.";
    if (!lead.phone.trim()) errs.phone = "Please enter a contact phone number.";
    if (!lead.address.trim()) errs.address = "Please enter your address.";
    if (!lead.consent) errs.consent = "Please tick the consent box to continue.";
    setLeadErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Email the generated report to the customer (Resend on the backend).
  const sendReportEmail = async (id, r) => {
    if (!id) return;
    setEmailStatus("sending");
    try {
      const res = await fetch(`${API}/leads/${id}/email-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ crm_prospect_id: crmProspectId || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "sent") {
        setEmailStatus("sent");
      } else if (res.ok && data.status === "skipped_no_provider") {
        // Email provider not configured yet — don't surface as a failure to the user.
        // Result page still works; backend admin can wire Resend later.
        setEmailStatus(null);
      } else {
        setEmailStatus("failed");
      }
    } catch {
      setEmailStatus("failed");
    }
  };

  const submit = async () => {
    if (!validateLead()) {
      toast.error("Please complete all required fields to continue.");
      return;
    }
    setSubmitting(true);
    try {
      // Compute the full estimate client-side using the configurable engine
      const r = calculateFullEstimate(inputs);

      // Render results immediately so the customer isn't blocked by network latency.
      setResult(r);
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Create the CRM prospect FIRST so its id can be saved with the lead — this
      // is what lets the Book Free Home Assessment button carry ?prospectId= both
      // on the results page and inside the report email. Awaited but isolated: a
      // CRM failure just yields a null id and never blocks lead creation below.
      let prospectId = null;
      try {
        const ad = lead.address_details;
        const crmPayload = {
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          // CRM gets the street line only — suburb/city/postcode are sent separately below.
          address: ad?.street_address || lead.address,
          suburb: lead.suburb || ad?.sublocality || ad?.locality || "",
          city: lead.city || ad?.locality || ad?.region || "",
          postcode: lead.postcode || ad?.postal_code || "",
          leadSource: isStaffMode
            ? (STAFF_SOURCE_TO_CRM[staffCapture.source] || "OTHER")
            : CRM_LEAD_SOURCE,
          numberOfPeopleInHousehold: Number(inputs.people) || null,
          savingsCalculatorResult: r.annual_saving,
        };
        // Enum fields only sent when they map to a valid CRM value.
        const crmSystem = CRM_SYSTEM_MAP[inputs.current_system];
        if (crmSystem) crmPayload.currentHotWaterSystem = crmSystem;
        const crmUsage = CRM_USAGE_MAP[inputs.other_usage];
        if (crmUsage) crmPayload.approximateHotWaterUsage = crmUsage;
        // Staff mode: attach the selected specialist as the assigned agent.
        if (isStaffMode && staffCapture.specialist_id) {
          crmPayload.assignAgentId = staffCapture.specialist_id;
        }
        const crmCreate = submitProspectCRM(crmPayload);
        // Update the page button the moment the id lands, even if it's late.
        crmCreate.then((id) => { if (id) setCrmProspectId(id); });
        // Wait briefly so the lead (and its auto-email) can include the id, but
        // never let a slow/unreachable CRM block lead creation beyond this.
        prospectId = await Promise.race([
          crmCreate,
          new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
        ]);
      } catch { /* CRM failure must not block lead creation */ }

      // Persist the lead, now carrying the CRM prospect id so the backend's
      // report email links its Book button to the same prospect.
      const leadPayload = {
        type: "calculator",
        mode: isStaffMode ? "staff" : "public",
        // Customer data
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        address: lead.address,
        suburb: lead.address_details?.locality || lead.address, // legacy compat + structured suburb when available
        region: lead.address_details?.region || undefined,
        postal_code: lead.address_details?.postal_code || undefined,
        country: lead.address_details?.country || undefined,
        latitude: lead.address_details?.lat ?? undefined,
        longitude: lead.address_details?.lng ?? undefined,
        consent: lead.consent,
        // Calculator inputs
        current_system: inputs.current_system,
        household_size: Number(inputs.people) || null,
        calculator_inputs: inputs,
        // Calculator outputs (minus the assumptions snapshot to keep payload lean)
        calculator_results: {
          annual_saving: r.annual_saving,
          monthly_saving: r.monthly_saving,
          fifteen_year_saving: r.fifteen_year_saving,
          current_annual_cost: r.current_annual_cost,
          heat_pump_annual_cost: r.heat_pump_annual_cost,
          co2_saving_kg: r.co2_saving_kg,
          recommended_system: r.recommended_system,
          confidence: r.confidence,
          cop_used: r.cop_used,
          installed_cost_low: r.installed_cost_low,
          installed_cost_high: r.installed_cost_high,
          installed_cost_used: r.installed_cost_used,
          break_even_year: r.break_even_year,
          total_return_after_life: r.total_return_after_life,
          system_life_years: r.system_life_years,
          finance: r.finance,
          annual_kwh_demand: r.annual_kwh_demand,
        },
        recommended_system: r.recommended_system,
        // CRM prospect id (when created) — used only for the email Book button link.
        crm_prospect_id: prospectId || undefined,
        // Internal staff capture (only present in staff mode)
        notes: isStaffMode ? `Staff capture: ${JSON.stringify(staffCapture)}` : undefined,
        internal_lead_data: isStaffMode ? staffCapture : undefined,
      };

      // Persist lead. The backend auto-attempts the report email when
      // RESEND_API_KEY is configured, so we use its returned status as the
      // source of truth — avoids accidental duplicate sends.
      submitLead(leadPayload)
        .then((res) => {
          const id = res?.id || null;
          setLeadId(id);
          const backendStatus = res?.email_status; // 'sent' | 'failed' | 'skipped_no_provider' | undefined
          if (backendStatus === "sent") {
            setEmailStatus("sent");
          } else if (backendStatus === "failed") {
            setEmailStatus("failed");
          }
          // For 'skipped_no_provider' / undefined: leave emailStatus null so the
          // CTA button reads "Email My Estimate" (manual resend stays available).
        })
        .catch(() => { /* non-blocking — local result still shown */ });
    } catch {
      toast.error("Calculation failed. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* PREMIUM BRANDED HERO */}
      <section data-testid="calc-hero" className="relative overflow-hidden bg-[#0B132B]">
        <div className="absolute inset-0">
          <img
            src="/hero/home_hero.png"
            alt="Modern Kiwi dusk home with NeoPower hot water heat pump"
            className="w-full h-full object-cover object-right opacity-60"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/90 to-[#0B132B]/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B132B]/40" />
        </div>
        <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#10B981]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/15 text-[#34D399] text-[11px] font-semibold tracking-widest uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-dot" /> Savings Calculator
          </span>
          <h1 className="font-heading mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-bold tracking-tight text-white leading-[1.05]">
            Hot Water <span className="text-[#10B981]">Savings Calculator</span>
            <br className="hidden sm:block" />
            <span className="text-white/85 text-xl sm:text-2xl lg:text-[28px] font-semibold">by AeroMax Energy</span>
          </h1>
          <p className="mt-5 text-[15px] sm:text-lg text-white/80 leading-relaxed max-w-2xl">
            Estimate the potential annual running cost savings when replacing your old hot water system with a NeoPower Black Shield heat pump — built around real NZ household conditions.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/75">
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> {totalSteps}-step guided estimate</span>
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Transparent assumptions</span>
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> No guesswork</span>
          </div>
          <p className="mt-5 text-[11px] text-white/55 max-w-2xl leading-relaxed">
            Estimates are based on typical NZ household assumptions and conservative energy price settings. Actual savings may vary depending on household usage, tariffs, installation conditions and system performance.
          </p>
          {isStaffMode && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span
                data-testid="hero-staff-badge"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-[11px] font-semibold tracking-widest uppercase"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" /> Staff Mode · Internal Use
              </span>
              <button
                type="button"
                data-testid="exit-staff-mode"
                onClick={exitStaffMode}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white/85 hover:text-white text-[11px] font-semibold tracking-widest uppercase transition-colors"
              >
                <LogOut className="h-3 w-3" /> Exit Staff Mode
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CALCULATOR BODY */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          {!result && (
            <CalcStepCard
              stepNumber={step + 1}
              totalSteps={totalSteps}
              label={currentLabel}
              staffMode={isStaffMode && stepKey === "staff-lead"}
            >
              {/* STAFF LEAD CAPTURE */}
              {stepKey === "staff-lead" && (
                <div className="space-y-5">
                  <p className="text-sm text-[#475569] -mt-2 mb-2">For internal AeroMax use — home shows, follow-up, referrals and partner-generated leads.</p>

                  <Field label="AeroMax Energy Saving Specialist" icon={Users}>
                    <select
                      value={staffCapture.specialist_id || (staffCapture.specialist === "Other" ? "__other__" : "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__other__") {
                          setStaffCapture((s) => ({ ...s, specialist_id: "", specialist: "Other" }));
                        } else if (!val) {
                          setStaffCapture((s) => ({ ...s, specialist_id: "", specialist: "" }));
                        } else {
                          const a = agents.find((x) => x.id === val);
                          setStaffCapture((s) => ({
                            ...s,
                            specialist_id: val,
                            specialist: a ? `${a.name || ""} ${a.lastName || ""}`.trim() : "",
                          }));
                        }
                      }}
                      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                    >
                      <option value="">Select a specialist…</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{`${a.name || ""} ${a.lastName || ""}`.trim()}</option>
                      ))}
                      <option value="__other__">Other</option>
                    </select>
                    {staffCapture.specialist === "Other" && (
                      <Input className="mt-2" placeholder="Enter specialist name" value={staffCapture.specialist_other} onChange={(e) => setStaff("specialist_other", e.target.value)} />
                    )}
                  </Field>

                  <Field label="AeroMax Installation Partner" icon={Wrench}>
                    <select
                      value={staffCapture.installer}
                      onChange={(e) => setStaff("installer", e.target.value)}
                      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                    >
                      {STAFF_INSTALLERS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                    {staffCapture.installer === "other" && (
                      <Input className="mt-2" placeholder="Enter partner name" value={staffCapture.installer_other} onChange={(e) => setStaff("installer_other", e.target.value)} />
                    )}
                  </Field>

                  <Field label="Date of Initiation" icon={Calendar}>
                    <Input
                      type="date"
                      value={staffCapture.date_initiation}
                      onChange={(e) => setStaff("date_initiation", e.target.value)}
                      className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                  </Field>

                  <Field label="Source" icon={HousePlug}>
                    <select
                      value={staffCapture.source}
                      onChange={(e) => setStaff("source", e.target.value)}
                      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                    >
                      {STAFF_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>

                  {staffCapture.source === "Home Show" && (
                    <Field label="Home Show Name">
                      <select
                        value={staffCapture.home_show}
                        onChange={(e) => setStaff("home_show", e.target.value)}
                        className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                      >
                        {STAFF_HOME_SHOWS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {staffCapture.home_show === "Other" && (
                        <Input className="mt-2" placeholder="Enter home show name" value={staffCapture.home_show_other} onChange={(e) => setStaff("home_show_other", e.target.value)} />
                      )}
                    </Field>
                  )}

                  {staffCapture.source === "Referral" && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Referral Code">
                        <Input value={staffCapture.referral_code} onChange={(e) => setStaff("referral_code", e.target.value)} />
                      </Field>
                      <Field label="Referral Name / Notes">
                        <Input value={staffCapture.referral_name} onChange={(e) => setStaff("referral_name", e.target.value)} />
                      </Field>
                    </div>
                  )}

                  {staffCapture.source === "Digital" && (
                    <Field label="Digital Source">
                      <select
                        value={staffCapture.digital_source}
                        onChange={(e) => setStaff("digital_source", e.target.value)}
                        className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                      >
                        {STAFF_DIGITAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {staffCapture.digital_source === "Other" && (
                        <Input className="mt-2" placeholder="Enter digital source" value={staffCapture.digital_source_other} onChange={(e) => setStaff("digital_source_other", e.target.value)} />
                      )}
                    </Field>
                  )}

                  {staffCapture.source === "Other" && (
                    <Field label="Source — please specify">
                      <Input value={staffCapture.source_other} onChange={(e) => setStaff("source_other", e.target.value)} />
                    </Field>
                  )}
                </div>
              )}

              {/* HOME & HOT WATER HABITS */}
              {stepKey === "habits" && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="People in home" icon={Users} helper="Total residents who use hot water.">
                    <Input
                      data-testid="calc-people"
                      type="number"
                      min="1"
                      max="20"
                      placeholder="Enter number of people"
                      value={inputs.people}
                      onChange={(e) => set("people", e.target.value)}
                    />
                  </Field>
                  <Field label="Showers per day (total)" icon={Droplets} helper="Across the whole household.">
                    <Input
                      data-testid="calc-showers"
                      type="number"
                      min="0"
                      max="40"
                      placeholder="Enter showers per day"
                      value={inputs.showers_per_day}
                      onChange={(e) => set("showers_per_day", e.target.value)}
                    />
                  </Field>
                  <Field label="Avg shower length (minutes)" icon={CalendarClock}>
                    <Input
                      data-testid="calc-shower-len"
                      type="number"
                      min="1"
                      max="30"
                      placeholder="Enter minutes"
                      value={inputs.shower_length_min}
                      onChange={(e) => set("shower_length_min", e.target.value)}
                    />
                  </Field>
                  <Field label="Shower flow (L/min)" icon={Droplets}>
                    <select
                      data-testid="calc-flow"
                      value={inputs.shower_flow_lpm}
                      onChange={(e) => set("shower_flow_lpm", Number(e.target.value))}
                      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm"
                    >
                      <option value={6}>6 (efficient)</option>
                      <option value={7.5}>7.5</option>
                      <option value={9}>9 (NZ default)</option>
                      <option value={12}>12 (high flow)</option>
                    </select>
                  </Field>
                  <Field label="Other hot water usage" icon={Wifi} helper="Dishes, basin & laundry combined.">
                    <select data-testid="calc-other" value={inputs.other_usage} onChange={(e) => set("other_usage", e.target.value)} className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </Field>
                  <Field label="Bathrooms" icon={Bath}>
                    <Input
                      data-testid="calc-bathrooms"
                      type="number"
                      min="1"
                      max="10"
                      placeholder="Enter number of bathrooms"
                      value={inputs.bathrooms}
                      onChange={(e) => set("bathrooms", e.target.value)}
                    />
                  </Field>
                  <Field label="Bath / spa use" icon={Bath}>
                    <select data-testid="calc-bath" value={inputs.bath_use} onChange={(e) => set("bath_use", e.target.value)} className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm">
                      <option value="none">None</option>
                      <option value="occasional">Occasional</option>
                      <option value="frequent">Frequent</option>
                    </select>
                  </Field>
                </div>
              )}

              {/* CURRENT HOT WATER SYSTEM */}
              {stepKey === "current-system" && (
                <div className="space-y-7">
                  <div>
                    <p className="font-heading text-base font-semibold text-[#0B132B] mb-3">Select your current / old hot water system</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {SYSTEM_OPTIONS.map((opt) => (
                        <CalcChoiceCard
                          key={opt.v}
                          testId={`calc-sys-${opt.v}`}
                          icon={opt.icon}
                          title={opt.title}
                          description={opt.desc}
                          selected={inputs.current_system === opt.v}
                          onClick={() => set("current_system", opt.v)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-heading text-base font-semibold text-[#0B132B] mb-3">How old is your current / old hot water system?</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                      {SYSTEM_AGE_OPTIONS.map((opt) => (
                        <CalcChoiceCard
                          key={opt.v}
                          testId={`calc-age-${opt.v}`}
                          title={opt.title}
                          selected={inputs.system_age === opt.v}
                          onClick={() => set("system_age", opt.v)}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ENERGY COSTS & SAVINGS POTENTIAL */}
              {stepKey === "energy" && (
                <div className="space-y-7">
                  <div>
                    <p className="font-heading text-base font-semibold text-[#0B132B] mb-3">What is your current average monthly electricity bill?</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {BILL_RANGE_OPTIONS.map((opt) => (
                        <CalcChoiceCard
                          key={opt.v}
                          testId={`calc-bill-${opt.v}`}
                          title={opt.title}
                          selected={inputs.bill_range === opt.v}
                          onClick={() => set("bill_range", opt.v)}
                          compact
                        />
                      ))}
                    </div>
                  </div>

                  {/* Adaptive cost inputs */}
                  <Card className="p-5 rounded-2xl border-[#E2E8F0] bg-[#F8FAFC]">
                    <p className="font-heading text-sm font-semibold text-[#0B132B] mb-4 flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-[#10B981]" /> Current energy cost
                    </p>

                    {["continuous_gas", "gas_storage"].includes(inputs.current_system) && (
                      <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field label="Mains gas daily charge (c/day)">
                            <Input
                              data-testid="calc-gas-daily"
                              type="number"
                              step="0.1"
                              placeholder="Enter daily charge"
                              value={inputs.daily_gas_charge_cents}
                              onChange={(e) => set("daily_gas_charge_cents", e.target.value)}
                            />
                          </Field>
                          <Field label="Variable gas usage charge">
                            <Input
                              data-testid="calc-gas-price"
                              type="number"
                              step="0.01"
                              placeholder="Enter usage rate"
                              value={inputs.gas_price}
                              onChange={(e) => set("gas_price", e.target.value)}
                            />
                          </Field>
                        </div>
                        <Field label="Gas usage rate unit">
                          <select value={inputs.gas_unit} onChange={(e) => set("gas_unit", e.target.value)} className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm">
                            <option value="c_per_mj">cents per MJ</option>
                            <option value="c_per_kwh">cents per kWh</option>
                          </select>
                        </Field>
                        <p className="text-xs text-[#475569] leading-relaxed">Both your daily fixed gas charges and usage charges are included in the comparison.</p>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#10B981]/5 border border-[#10B981]/20">
                          <Checkbox data-testid="calc-remove-gas" id="remove-gas" checked={inputs.remove_gas_connection} onCheckedChange={(v) => set("remove_gas_connection", !!v)} />
                          <Label htmlFor="remove-gas" className="text-sm cursor-pointer">
                            I may disconnect gas supply (include daily gas charge savings)
                          </Label>
                        </div>
                      </div>
                    )}

                    {inputs.current_system === "lpg" && (
                      <div className="space-y-4">
                        <Field label="Bottled LPG cost / 45kg bottle ($)" helper="Approximate average per bottle.">
                          <Input
                            data-testid="calc-lpg"
                            type="number"
                            step="1"
                            placeholder="Enter bottle cost"
                            value={inputs.lpg_bottle_cost}
                            onChange={(e) => set("lpg_bottle_cost", e.target.value)}
                          />
                        </Field>
                        <p className="text-xs text-[#475569] leading-relaxed">Bottled gas costs vary by region and supplier — AeroMax can confirm during assessment.</p>
                      </div>
                    )}

                    {inputs.current_system === "electric_storage" && (
                      <div className="space-y-4">
                        <Field label="Electricity tariff (c/kWh)">
                          <Input
                            data-testid="calc-elec"
                            type="number"
                            step="0.1"
                            placeholder="Enter tariff"
                            value={inputs.electricity_c_per_kwh}
                            onChange={(e) => set("electricity_c_per_kwh", e.target.value)}
                          />
                        </Field>
                        <p className="text-xs text-[#475569] leading-relaxed">We use a typical hot-water electricity share to estimate your current cylinder running cost.</p>
                      </div>
                    )}

                    {inputs.current_system === "not_sure" && (
                      <div className="p-4 rounded-xl bg-white border border-[#E2E8F0]">
                        <p className="text-sm text-[#475569] leading-relaxed">
                          We'll use conservative NZ household assumptions. AeroMax can confirm your existing setup during assessment.
                        </p>
                      </div>
                    )}

                    {/* Always show electricity tariff if non-electric so heat pump cost can be calculated */}
                    {inputs.current_system !== "electric_storage" && inputs.current_system !== "not_sure" && (
                      <div className="mt-4">
                        <Field label="Your electricity tariff (c/kWh)" helper="Used to estimate the NeoPower running cost.">
                          <Input
                            data-testid="calc-elec"
                            type="number"
                            step="0.1"
                            placeholder="Enter tariff"
                            value={inputs.electricity_c_per_kwh}
                            onChange={(e) => set("electricity_c_per_kwh", e.target.value)}
                          />
                        </Field>
                      </div>
                    )}
                  </Card>

                  <div>
                    <p className="font-heading text-base font-semibold text-[#0B132B] mb-3">Would you be interested in lowering your hot water cost by 70–80%?</p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {LOWER_BILL_OPTIONS.map((opt) => (
                        <CalcChoiceCard
                          key={opt.v}
                          testId={`calc-lower-${opt.v}`}
                          title={opt.title}
                          description={opt.desc}
                          selected={inputs.lower_bill_interest === opt.v}
                          onClick={() => set("lower_bill_interest", opt.v)}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* BEST-FIT NEOPOWER SOLUTION */}
              {stepKey === "best-fit" && (
                <div className="space-y-6">
                  {/* Recommendation card */}
                  <div className="relative rounded-3xl overflow-hidden border border-[#0B132B]/10 bg-gradient-to-br from-[#0B132B] to-[#050914] text-white shadow-[0_30px_80px_-30px_rgba(11,19,43,0.55)]">
                    <div className="grid md:grid-cols-12">
                      <div className="md:col-span-7 p-6 sm:p-8 relative z-10">
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-[10px] font-bold tracking-widest uppercase">
                          <Sparkles className="h-3 w-3" /> AeroMax Recommended
                        </span>
                        <h3 className="font-heading mt-3 text-2xl sm:text-[28px] font-bold leading-[1.1]">
                          NeoPower Black Shield <span className="text-[#10B981]">270L</span>
                        </h3>
                        <p className="mt-3 text-white/80 text-sm leading-relaxed max-w-md">
                          For most Kiwi households, choosing the NeoPower Black Shield 270L is a <span className="text-white font-semibold">no-brainer</span> — premium efficiency, lower running costs and one of New Zealand's most efficient all-in-one hot water heat pump systems.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          {[
                            { l: "COP", v: "5.4" },
                            { l: "Capacity", v: "270L" },
                            { l: "Silent", v: "42 dBa" },
                            { l: "Warranty", v: "7 yr" },
                          ].map((s, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs">
                              <span className="text-white/60">{s.l}</span>
                              <span className="font-semibold text-white">{s.v}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="md:col-span-5 relative min-h-[220px] md:min-h-[300px]">
                        <img src={PRODUCT_IMG} alt="NeoPower Black Shield 270L" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0B132B]/65 md:to-[#0B132B]/45" />
                      </div>
                    </div>
                  </div>

                  {/* Key benefit cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      { icon: Shield, t: "Premium all-in-one" },
                      { icon: Cpu, t: "COP 5.4 efficiency" },
                      { icon: Leaf, t: "Efficient & sustainable" },
                      { icon: HousePlug, t: "Modern NZ homes" },
                      { icon: Sparkles, t: "Smarter replacement" },
                    ].map((b, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-white border border-[#E2E8F0] text-center hover:border-[#10B981]/40 hover:shadow-md transition-all">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981] mb-2">
                          <b.icon className="h-5 w-5" strokeWidth={1.8} />
                        </span>
                        <p className="text-xs font-semibold text-[#0B132B] leading-tight">{b.t}</p>
                      </div>
                    ))}
                  </div>

                  {/* Finance question */}
                  <div className="p-5 sm:p-7 rounded-3xl border border-[#E2E8F0] bg-white">
                    <div className="flex items-start gap-3 mb-1">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981] shrink-0">
                        <Banknote className="h-5 w-5" />
                      </span>
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Optional</p>
                        <p className="font-heading text-base sm:text-lg font-semibold text-[#0B132B] leading-snug mt-0.5">
                          Would you like to explore monthly repayment options through NZ Green Loan finance?
                        </p>
                        <p className="text-xs text-[#475569] mt-1.5 leading-relaxed">
                          Finance is optional and subject to lender eligibility and approval. AeroMax can help you understand available options — finance approval is handled by the lender.
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
                      {FINANCE_OPTIONS.map((opt) => (
                        <CalcChoiceCard
                          key={opt.v}
                          testId={`calc-finance-${opt.v}`}
                          title={opt.title}
                          selected={inputs.finance_option === opt.v}
                          onClick={() => set("finance_option", opt.v)}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ASSESSMENT DETAILS */}
              {stepKey === "assessment" && (
                <div className="space-y-5">
                  <p className="text-sm text-[#475569] -mt-2 mb-2">All fields are required. Enter your details to view your full assessment.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name *" error={leadErrors.name}>
                      <Input
                        data-testid="calc-lead-name"
                        value={lead.name}
                        onChange={(e) => { setLead({ ...lead, name: e.target.value }); if (leadErrors.name) setLeadErrors({ ...leadErrors, name: undefined }); }}
                        placeholder="Enter your full name"
                        aria-invalid={!!leadErrors.name}
                      />
                    </Field>
                    <Field label="Email *" error={leadErrors.email}>
                      <Input
                        data-testid="calc-lead-email"
                        type="email"
                        value={lead.email}
                        onChange={(e) => { setLead({ ...lead, email: e.target.value }); if (leadErrors.email) setLeadErrors({ ...leadErrors, email: undefined }); }}
                        placeholder="you@example.com"
                        aria-invalid={!!leadErrors.email}
                      />
                    </Field>
                    <Field label="Phone *" error={leadErrors.phone}>
                      <Input
                        data-testid="calc-lead-phone"
                        type="tel"
                        value={lead.phone}
                        onChange={(e) => { setLead({ ...lead, phone: e.target.value }); if (leadErrors.phone) setLeadErrors({ ...leadErrors, phone: undefined }); }}
                        placeholder="Enter contact phone"
                        aria-invalid={!!leadErrors.phone}
                      />
                    </Field>
                    <Field label="Address *" icon={Building2} helper="Start typing for NZ address suggestions. You can also type your address manually." error={leadErrors.address}>
                      <AddressAutocomplete
                        data-testid="calc-lead-address"
                        value={lead.address}
                        onChange={(v) => {
                          setLead((s) => ({ ...s, address: v }));
                          if (leadErrors.address) setLeadErrors({ ...leadErrors, address: undefined });
                        }}
                        onPlaceSelected={(p) => {
                          setLead((s) => ({
                            ...s,
                            address: p.formatted_address || s.address,
                            suburb: p.sublocality || p.locality || s.suburb,
                            city: p.locality || s.city,
                            postcode: p.postal_code || s.postcode,
                            address_details: {
                              formatted_address: p.formatted_address,
                              street_address: p.street_address,
                              sublocality: p.sublocality,
                              locality: p.locality,
                              region: p.region,
                              postal_code: p.postal_code,
                              country: p.country,
                              lat: p.lat,
                              lng: p.lng,
                            },
                          }));
                          if (leadErrors.address) setLeadErrors({ ...leadErrors, address: undefined });
                        }}
                        placeholder="Street, suburb, city"
                        aria-invalid={!!leadErrors.address}
                        autoComplete="off"
                      />
                    </Field>
                    <Field label="Suburb" icon={Building2} helper="Auto-filled from your address — edit if needed.">
                      <Input
                        data-testid="calc-lead-suburb"
                        value={lead.suburb}
                        onChange={(e) => setLead((s) => ({ ...s, suburb: e.target.value }))}
                        placeholder="Suburb"
                      />
                    </Field>
                    <Field label="City" icon={Building2}>
                      <Input
                        data-testid="calc-lead-city"
                        value={lead.city}
                        onChange={(e) => setLead((s) => ({ ...s, city: e.target.value }))}
                        placeholder="City / town"
                      />
                    </Field>
                    <Field label="Postcode" icon={Building2}>
                      <Input
                        data-testid="calc-lead-postcode"
                        value={lead.postcode}
                        onChange={(e) => setLead((s) => ({ ...s, postcode: e.target.value }))}
                        placeholder="Postcode"
                      />
                    </Field>
                  </div>
                  <div className={`flex items-start gap-3 p-4 rounded-xl border ${leadErrors.consent ? "border-red-300 bg-red-50" : "border-[#E2E8F0] bg-[#F8FAFC]"}`}>
                    <Checkbox
                      data-testid="calc-consent"
                      id="consent"
                      checked={lead.consent}
                      onCheckedChange={(v) => { setLead({ ...lead, consent: !!v }); if (leadErrors.consent) setLeadErrors({ ...leadErrors, consent: undefined }); }}
                    />
                    <Label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed text-[#0B132B]">
                      I agree for AeroMax Energy to contact me about my hot water estimate and suitable upgrade options. *
                    </Label>
                  </div>
                  {leadErrors.consent && (
                    <p data-testid="calc-consent-error" className="text-xs text-red-600 -mt-3">{leadErrors.consent}</p>
                  )}
                  <p className="text-[11px] text-[#475569] leading-relaxed">
                    Your information is safe with us. We will not share your details with third parties without your consent.
                  </p>
                </div>
              )}

              {/* NAV BUTTONS */}
              <div className="mt-10 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={prev}
                  disabled={step === 0}
                  data-testid="calc-prev"
                  className="rounded-full px-6 py-5 border-[#E2E8F0]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {!isLastStep ? (
                  <Button
                    onClick={next}
                    data-testid="calc-next"
                    className="rounded-full bg-[#0B132B] hover:bg-[#050914] text-white px-7 py-5 font-semibold"
                  >
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    data-testid="calc-submit"
                    disabled={submitting}
                    className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-5 font-semibold shadow-lg shadow-emerald-500/25"
                  >
                    {submitting ? "Calculating..." : "Show My Assessment"} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CalcStepCard>
          )}

          {result && (
            <CalcResults
              result={result}
              lead={lead}
              inputs={inputs}
              onRunAgain={() => { setResult(null); setStep(0); setLeadId(null); setEmailStatus(null); setCrmProspectId(null); }}
              staffMode={isStaffMode}
              staffData={isStaffMode ? staffCapture : null}
              emailStatus={emailStatus}
              crmProspectId={crmProspectId}
              onEmailReport={leadId ? () => sendReportEmail(leadId, result) : undefined}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, icon: Icon, helper, error, children }) {
  return (
    <div>
      <Label className="text-xs flex items-center gap-1.5 mb-1.5 text-[#0B132B] font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5 text-[#10B981]" />} {label}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] text-red-600 mt-1.5 font-medium" role="alert">{error}</p>
      ) : (
        helper && <p className="text-[11px] text-[#475569] mt-1.5">{helper}</p>
      )}
    </div>
  );
}
